/* eslint-disable max-lines */
import {
  DISPUTE_PROCESSOR_STATUS,
  PROCESSOR_REFUND_STATUS
} from "../../components/change-history/enumValuesPayments";
import {
  ICancellationRefusal,
  IReservationRefund
} from "../../interfaces/reservations/Cancellation.types";
import { formatRefundDeadline, refundGraceState } from "./refundGrace";

type RefusalKind =
  | "dispute"
  | "esim"
  | "seats"
  | "operator"
  | "invoiced"
  | "spent"
  | "paid"
  | "window"
  | "nothing"
  | "stale";

// The backend's CancellationCodes, exactly.
const KINDS: Record<string, RefusalKind> = {
  "quote.stale": "stale",
  "dispute.open": "dispute",
  "reconfirm.esim_deactivated": "esim",
  "reconfirm.seats_gone": "seats",
  "reconfirm.operator_booking_cancelled": "operator",
  "reconfirm.operator_invoiced": "invoiced",
  "reconfirm.credits_spent": "spent",
  "reconfirm.refund_paid": "paid",
  "reconfirm.window_passed": "window",
  "reconfirm.nothing_to_undo": "nothing"
};

export const refusalKind = (code: string | null | undefined): RefusalKind | null =>
  (code && KINDS[code.toLowerCase()]) || null;

const RECONFIRM_SENTENCES: Record<RefusalKind, string> = {
  paid: "Rimbursimi i është paguar tashmë klientit, prandaj rezervimi nuk mund të rikonfirmohet.",
  spent:
    "Klienti e ka shpenzuar tashmë Gjirafa Money që iu kthye, prandaj rezervimi nuk mund të " +
    "rikonfirmohet.",
  seats: "Vendet në këtë udhëtim nuk janë më të lira, prandaj rezervimi nuk mund të rikonfirmohet.",
  operator:
    "Rezervimi te operatori është anuluar tashmë, prandaj ky rezervim nuk mund të rikonfirmohet.",
  invoiced:
    "Anulimi është përfshirë tashmë në një faturë të operatorit, prandaj rezervimi nuk mund të " +
    "rikonfirmohet.",
  esim: "eSIM-i i këtij rezervimi është çaktivizuar, prandaj rezervimi nuk mund të rikonfirmohet.",
  dispute:
    "Klienti ka hapur një kontestim për këtë pagesë. Derisa të mbyllet, rezervimi nuk mund të " +
    "rikonfirmohet.",
  window: "Afati 24-orësh ka kaluar dhe rimbursimi është nisur, prandaj rezervimi nuk mund të rikonfirmohet.",
  nothing: "Ky rezervim nuk ka asnjë anulim që mund të kthehet.",
  stale: "Rezervimi ndryshoi ndërkohë. Rifreskoni faqen dhe provoni përsëri."
};

export const reconfirmRefusalSentence = (code: string | null | undefined): string => {
  const kind = refusalKind(code);
  return kind ? RECONFIRM_SENTENCES[kind] : "Rezervimi nuk u rikonfirmua. Provoni përsëri.";
};

const CANCELLATION_SENTENCES: Partial<Record<RefusalKind, string>> = {
  dispute:
    "Klienti ka hapur një kontestim për këtë pagesë: derisa të mbyllet, nuk kthehen para dhe as " +
    "Gjirafa Money.",
  stale: "Rezervimi ndryshoi ndërkohë. Shumat u rifreskuan — kontrollojini dhe konfirmoni përsëri."
};

// A preview refusal: our sentence where we know the code, the server's own otherwise.
export const cancellationRefusalSentence = (refusal: ICancellationRefusal): string => {
  const kind = refusalKind(refusal.code);
  return (kind && CANCELLATION_SENTENCES[kind]) || refusal.message || refusal.code;
};

// The backend's DisputeCodes, exactly.
const OPERATOR_PAYMENT_SENTENCES: Record<string, string> = {
  "dispute.not_lost": "Ky rezervim nuk ka më një kontestim të humbur; rifreskuam të dhënat.",
  "dispute.operator_invoiced":
    "Pjesa e operatorit është zbritur tashmë në një faturë të tij, prandaj zbritja nuk mund të " +
    "hiqet më nga këtu."
};

export const operatorPaymentRefusalSentence = (code: string | null | undefined): string =>
  (code && OPERATOR_PAYMENT_SENTENCES[code.toLowerCase()]) ||
  "Pagesa e operatorit nuk u ndryshua. Provoni përsëri.";

export type RefundTone = "muted" | "ok" | "warn" | "error";

export interface IRefundStatusView {
  label: string;
  tone: RefundTone;
  note?: string;
}

// A decision taken with "Pa rimbursim": nothing went to the card and no Gjirafa Money came back.
export const refundReturnedNothing = (refund: IReservationRefund): boolean =>
  refund.amount <= 0 && (refund.creditsReturned ?? 0) <= 0;

export const refundStatusView = (
  refund: IReservationRefund,
  now: number = Date.now()
): IRefundStatusView => {
  if (refundReturnedNothing(refund)) return { label: "Pa rimbursim", tone: "muted" };
  if (refund.isRefundInCredits) return { label: "Kthyer në Gjirafa Money", tone: "ok" };
  const confirmed = formatRefundDeadline(refund.processorConfirmedAtUtc);
  switch (refund.processorStatus) {
    case "Succeeded":
      return {
        label: confirmed ? `Paguar më ${confirmed}` : PROCESSOR_REFUND_STATUS.Succeeded,
        tone: "ok"
      };
    case "Pending":
      return { label: PROCESSOR_REFUND_STATUS.Pending, tone: "muted" };
    case "Failed":
      return {
        label: PROCESSOR_REFUND_STATUS.Failed,
        tone: "error",
        note:
          "Banka e refuzoi rimbursimin. Klientit duhet t'i paguhet me dorë — kontaktoni financat " +
          "(janë njoftuar)."
      };
    case "Canceled":
      return { label: PROCESSOR_REFUND_STATUS.Canceled, tone: "warn" };
    default:
      break;
  }
  const deadline = formatRefundDeadline(refund.refundScheduledForUtc);
  return refundGraceState(refund, now) === "open" && deadline
    ? { label: `Në pritje deri më ${deadline}`, tone: "muted" }
    : { label: "Në radhë për pagesë", tone: "muted" };
};

// Lower-cased: the banner reads it mid-sentence.
export const disputeStatusLabel = (status?: string | null): string | null => {
  if (!status) return null;
  return (DISPUTE_PROCESSOR_STATUS[status.toLowerCase()] ?? "I panjohur").toLowerCase();
};
